import { useDialogState } from '@app/src/hooks/mui-hooks'
import AssessmentDialog from '@app/src/pages/Assessments/AssessmentDialog'
import { FilterGroup, Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import React, { PropsWithChildren, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  ProgressTrackerAssessmentDialogData,
  ProgressTrackerFilterType,
  ProgressTrackerModalContext,
  ProgressTrackerModalContextProps,
} from './ProgressTrackerModalContext'

const ProgressTrackerModalContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, openModal, closeModal] = useDialogState(false)
  const [assessmentDialogData, setAssessmentDialogData] = useState<ProgressTrackerAssessmentDialogData>()
  const { formatMessage } = useIntl()

  const openProgressTrackerModal: ProgressTrackerModalContextProps['openProgressTrackerModal'] = (
    filterType,
    assessmentTemplate,
    periodNames,
    level,
    title,
  ) => {
    const baseFilters = [
      {
        name: 'assessmentTemplateId',
        filters: [{ operator: Operators.EqualTo, value: assessmentTemplate.id }],
      },
      {
        name: 'periodName',
        filters: [{ operator: Operators.In, value: periodNames }],
      },
    ]

    let additionalFilters: FilterGroup[] = []
    let dialogTitle = title

    switch (filterType) {
      case ProgressTrackerFilterType.Assessed:
        additionalFilters = [
          {
            name: 'totalScore',
            filters: [{ operator: Operators.IsNotNull }],
          },
        ]
        dialogTitle = dialogTitle || formatMessage({ id: 'general.assessed' })
        break

      case ProgressTrackerFilterType.SelfReported:
        additionalFilters = [
          {
            name: 'isScreened',
            filters: [{ operator: Operators.EqualTo, value: false }],
          },
        ]
        dialogTitle = dialogTitle || formatMessage({ id: 'general.selfReported' })
        break

      case ProgressTrackerFilterType.AiScreened:
        additionalFilters = [
          {
            name: 'isScreened',
            filters: [{ operator: Operators.EqualTo, value: true }],
          },
        ]
        dialogTitle = dialogTitle || formatMessage({ id: 'general.aiScreened' })
        break

      case ProgressTrackerFilterType.AssessmentLevel:
        if (level) {
          additionalFilters = [
            {
              name: 'totalLevel',
              filters: [{ operator: Operators.EqualTo, value: level }],
            },
          ]
          dialogTitle = dialogTitle || formatMessage({ id: 'schemas.progressTracker.level' }, { level })
        }
        break

      default:
        break
    }

    setAssessmentDialogData({
      assessmentTemplate,
      activeLevel: filterType === ProgressTrackerFilterType.AssessmentLevel ? level : undefined,
      periods: periodNames,
      filters: [...baseFilters, ...additionalFilters],
      title: dialogTitle || '',
    })

    if (!isOpen) openModal()
  }

  return (
    <ProgressTrackerModalContext.Provider
      value={{
        openProgressTrackerModal,
        assessmentDialogData,
        setAssessmentDialogData,
      }}
    >
      {children}
      {assessmentDialogData && (
        <AssessmentDialog
          isOpen={isOpen}
          closeModal={closeModal}
          assessmentTemplate={assessmentDialogData.assessmentTemplate}
          activeLevel={assessmentDialogData.activeLevel}
          periods={assessmentDialogData.periods}
          filters={assessmentDialogData.filters}
        />
      )}
    </ProgressTrackerModalContext.Provider>
  )
}

export default ProgressTrackerModalContextProvider
